import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, Upload, X, Ticket, Bot, Video, ImageIcon, CheckCircle2, ArrowLeft } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

const ISSUES = [
  { key: "deposit", label: "Deposit not received", proof: true },
  { key: "withdrawal", label: "Withdrawal not received", proof: true },
  { key: "investment", label: "Investment / plan issue" },
  { key: "referral", label: "Referral bonus issue" },
  { key: "account", label: "Account / login issue" },
  { key: "other", label: "Other issue" },
];
const fileUrl = (p) => `${BASE}/api/support/file?path=${encodeURIComponent(p)}`;
const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });

function Attachments({ items }) {
  if (!items?.length) return null;
  return (
    <div className="sup-attach">
      {items.map((a) => (a.kind === "video"
        ? <video key={a.path} src={fileUrl(a.path)} controls preload="metadata" data-testid="support-attachment-video" />
        : <a key={a.path} href={fileUrl(a.path)} target="_blank" rel="noreferrer"><img src={fileUrl(a.path)} alt={a.name || "proof"} data-testid="support-attachment-image" /></a>))}
    </div>
  );
}

function TicketCard({ t, isAdmin, onChange }) {
  const setStatus = async (status) => { await api.post(`/admin/support/tickets/${t.ticket_id}/status`, { status }); onChange(); };
  return (
    <div className={`sup-ticket ${t.status}`} data-testid={`support-ticket-${t.ticket_id}`}>
      <div className="sup-ticket-top">
        <span><Ticket size={13} /> <b>{t.ticket_id}</b></span>
        <span className={`status ${t.status === "open" ? "pending" : "active"}`} data-testid={`support-ticket-status-${t.ticket_id}`}>{t.status}</span>
      </div>
      <small>{t.category_label} · {fmt(t.created_at)}</small>
      {isAdmin && (
        <button className="mini-button" onClick={() => setStatus(t.status === "open" ? "resolved" : "open")} data-testid={`support-ticket-toggle-${t.ticket_id}`}>
          {t.status === "open" ? "Mark resolved" : "Reopen"}
        </button>
      )}
    </div>
  );
}

function ProofUploader({ proofs, setProofs, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (ref.current) ref.current.value = "";
    if (!f) return;
    const isVid = f.type.startsWith("video/");
    if (!isVid && !f.type.startsWith("image/")) return setErr("Only screenshots (images) or videos are allowed.");
    if (f.size > (isVid ? 10 : 5) * 1024 * 1024) return setErr(isVid ? "Video must be under 10 MB." : "Image must be under 5 MB.");
    setErr(""); setBusy(true);
    const form = new FormData(); form.append("file", f);
    try {
      const r = await api.post("/support/proof", form, { headers: { "Content-Type": "multipart/form-data" } });
      setProofs([...proofs, r.data]);
      onUploaded?.();
    } catch (er) { setErr(er.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="sup-proofs">
      <div className="sup-proof-list">
        {proofs.map((p, i) => (
          <span key={p.path} className="sup-proof-chip" data-testid={`support-proof-chip-${i}`}>
            {p.kind === "video" ? <Video size={12} /> : <ImageIcon size={12} />} {p.name || p.kind}
            <button type="button" onClick={() => setProofs(proofs.filter((x) => x.path !== p.path))} data-testid={`support-proof-remove-${i}`}><X size={11} /></button>
          </span>
        ))}
      </div>
      {proofs.length < 5 && (
        <button type="button" className="button ghost sup-upload-btn" disabled={busy} onClick={() => ref.current?.click()} data-testid="support-proof-upload-button">
          <Upload size={14} /> {busy ? "Uploading…" : "Upload screenshot / video"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*,video/*" onChange={pick} style={{ display: "none" }} data-testid="support-proof-input" />
      <small className="sup-hint">Images up to 5 MB · videos up to 10 MB · max 5 files</small>
      {err && <div className="error-box" data-testid="support-proof-error">{err}</div>}
    </div>
  );
}

function IssueWizard({ onCreated, onCancel }) {
  const [issue, setIssue] = useState(null);
  const [desc, setDesc] = useState("");
  const [proofs, setProofs] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  if (!issue) {
    return (
      <div className="sup-options" data-testid="support-issue-options">
        {ISSUES.map((x, i) => (
          <button key={x.key} className="sup-option" onClick={() => setIssue(x)} data-testid={`support-issue-${x.key}`}>
            <b>{i + 1}</b> {x.label}
          </button>
        ))}
        {onCancel && <button className="quiet-button" onClick={onCancel} data-testid="support-wizard-cancel">Cancel</button>}
      </div>
    );
  }
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (issue.proof && !proofs.length) return setErr("Please upload a screenshot or video as proof for this issue.");
    setBusy(true);
    try {
      await api.post("/support/tickets", { category: issue.key, description: desc, proofs });
      onCreated();
    } catch (er) { setErr(er.response?.data?.detail?.toString() || "Could not create ticket"); }
    setBusy(false);
  };
  return (
    <form className="sup-form" onSubmit={submit} data-testid="support-ticket-form">
      <button type="button" className="back-link sup-back" onClick={() => setIssue(null)} data-testid="support-wizard-back"><ArrowLeft size={12} /> Change issue</button>
      <h4>{issue.label}</h4>
      <p className="sup-hint">Please upload a screenshot or video of the problem{issue.proof ? " (required)" : " (optional)"} and describe it briefly.</p>
      <textarea required minLength={3} rows={3} value={desc} onChange={(e) => setDesc(e.target.value)}
        placeholder="e.g. I sent 100 USDT at 2pm, TxID 0x…" data-testid="support-ticket-description" />
      <ProofUploader proofs={proofs} setProofs={setProofs} onUploaded={() => setErr("")} />
      {err && <div className="error-box" data-testid="support-ticket-error">{err}</div>}
      <button className="button primary" disabled={busy} data-testid="support-ticket-submit"><Ticket size={14} /> {busy ? "Creating…" : "Generate ticket"}</button>
    </form>
  );
}

function ChatLog({ messages, isAdmin, greeting }) {
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);
  return (
    <div className="chat-log" data-testid="support-log">
      {greeting && (
        <div className="chat-msg other bot" data-testid="support-greeting">
          <div><Bot size={13} /> {greeting}</div>
        </div>
      )}
      {messages.map((m) => (
        <div key={m.message_id} className={`chat-msg ${m.sender === (isAdmin ? "admin" : "user") ? "self" : "other"} ${m.auto ? "bot" : ""}`}
          data-testid={m.auto ? "support-auto-reply" : undefined}>
          <div>{m.auto && <CheckCircle2 size={13} />} {m.message}</div>
          <Attachments items={m.attachments} />
          <small>{new Date(m.created_at).toLocaleString()}{m.auto ? " · Auto reply" : ""}</small>
        </div>
      ))}
      {!greeting && !messages.length && <div className="empty-state small"><MessageCircle size={22} /><b>No messages yet</b></div>}
      <div ref={end} />
    </div>
  );
}

export function SupportPanel({ isAdmin, userId, user, embedded = false }) {
  const [messages, setMessages] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [text, setText] = useState("");
  const load = useCallback(async () => {
    const r = await api.get(isAdmin ? `/admin/support/${userId}` : "/support").catch(() => null);
    if (r) { setMessages(r.data.messages || []); setTickets(r.data.tickets || []); }
    setLoaded(true);
  }, [isAdmin, userId]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (isAdmin) await api.post("/admin/support", { user_id: userId, message: text });
    else await api.post("/support", { message: text });
    setText(""); load();
  };
  const first = (user?.name || "there").split(" ")[0];
  const showWizard = !isAdmin && loaded && (wizard || tickets.length === 0);
  return (
    <section className={embedded ? "" : "panel wide"} data-testid="support-panel">
      <div className="eyebrow">SUPPORT</div>
      <h2>{isAdmin ? `Conversation with ${userId}` : "Chat with the Nexa Pro team"}</h2>
      {tickets.length > 0 && <div className="sup-ticket-row">{tickets.map((t) => <TicketCard key={t.ticket_id} t={t} isAdmin={isAdmin} onChange={load} />)}</div>}
      <ChatLog messages={messages} isAdmin={isAdmin}
        greeting={isAdmin ? null : `Hello ${first}! Welcome to Nexa Pro support. What would you like to talk about? Choose an option below.`} />
      {showWizard && <IssueWizard onCreated={() => { setWizard(false); load(); }} onCancel={tickets.length ? () => setWizard(false) : null} />}
      {!isAdmin && loaded && !showWizard && (
        <button className="mini-button sup-new-issue" onClick={() => setWizard(true)} data-testid="support-new-issue-button"><Ticket size={12} /> Report a new issue</button>
      )}
      {(isAdmin || tickets.length > 0) && (
        <form className="chat-input" onSubmit={send}>
          <input data-testid="support-message-input" placeholder="Type your message…" value={text} onChange={(e) => setText(e.target.value)} />
          <button className="button primary" data-testid="support-send-button">Send</button>
        </form>
      )}
    </section>
  );
}

export function useSupportUnread(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/admin/support/unread-count").then((r) => setCount(r.data.unread_chats)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("nexa-support-read", load);
    return () => { clearInterval(t); window.removeEventListener("nexa-support-read", load); };
  }, [enabled]);
  return count;
}

export function AdminSupport() {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/admin/support").then((r) => setConvos(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const newChats = convos.filter((c) => c.unread > 0).length;
  return (
    <div className="content-grid">
      <section className="panel">
        <div className="eyebrow">SUPPORT</div>
        <h2>Conversations {newChats > 0 && <span className="sup-new-pill" data-testid="admin-support-new-count">{newChats} new</span>}</h2>
        {convos.length ? convos.map((c) => (
          <button key={c.user_id} onClick={() => { setSelected(c.user_id); setTimeout(() => { load(); window.dispatchEvent(new Event("nexa-support-read")); }, 800); }} data-testid={`support-convo-${c.user_id}`}
            className={`convo-row ${selected === c.user_id ? "chosen" : ""} ${c.unread > 0 ? "has-unread" : ""}`}>
            <div>
              <b>{c.user_name || c.user_id}</b>
              <small>{c.last_message?.slice(0, 60)}</small>
              {c.open_tickets > 0 && <em className="sup-open-tag">{c.open_tickets} open ticket{c.open_tickets > 1 ? "s" : ""}</em>}
            </div>
            {c.unread > 0 && <span className="count-badge" data-testid={`support-convo-unread-${c.user_id}`}>{c.unread}</span>}
          </button>
        )) : <div className="empty-state"><b>No conversations</b></div>}
      </section>
      <section className="panel wide">
        {selected ? <SupportPanel key={selected} isAdmin userId={selected} embedded /> : <div className="empty-state"><MessageCircle size={22} /><b>Select a conversation</b></div>}
      </section>
    </div>
  );
}
